import { Step, StepLabel, Stepper, Box, useMediaQuery, useTheme, Typography, Tooltip } from "@mui/material";
import { Cached, Flag } from "@mui/icons-material";
import { steps } from "./const/job-steps.const";
import { colors, roleColors } from "../../../ui/colors";

type JobStatusStepProps = {
  displayStepIndex: number;
  realStepIndex: number;
  maxAccessibleStepIndex: number;
  handleStepClick: (index: number) => void;
};

export const JobStatusStep = ({ displayStepIndex, realStepIndex, maxAccessibleStepIndex, handleStepClick }: JobStatusStepProps) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const inProgressIndex = steps.findIndex((s) => s.value === "inProgress");

  return (
    <Box
      sx={{
        width: "100%",
        overflowX: isXs ? "auto" : "visible",
      }}
    >
      <Box
        sx={{
          minWidth: isXs ? 560 : "100%",
          mx: "auto",
          my: 2,
        }}
      >
        <Stepper
          activeStep={displayStepIndex}
          alternativeLabel={!isXs}
          orientation={isXs ? "vertical" : "horizontal"}
          sx={{ "& .MuiStepConnector-alternativeLabel": { top: 15 } }}
        >
          {steps.map((step, index) => {
            const isClickable = index <= maxAccessibleStepIndex;
            const isActive = index === displayStepIndex;
            const isCurrent = index === realStepIndex;
            const isCompleted = index < realStepIndex;
            const isLastStep = index === steps.length - 1;
            const stepColor = isCurrent
              ? colors.orange.base
              : isCompleted
                ? roleColors.residential.base
                : colors.gray[300];

            let statusText = "";
            if (isActive && index !== realStepIndex) statusText = "VIEWING";
            else if (isCompleted) statusText = "COMPLETED";
            else if (isCurrent) statusText = "CURRENT STEP";
            else if (isActive) statusText = "VIEWING";

            return (
              <Step
                key={step.value}
                onClick={() => {
                  if (isClickable) handleStepClick(index);
                }}
                sx={{
                  cursor: isClickable ? "pointer" : "default",
                }}
              >
                <Tooltip title={isClickable ? `View ${step.label} details` : `${step.label} is not available yet`} placement="top" arrow>
                <StepLabel
                  icon={
                    isLastStep ? (
                      <Flag
                        sx={{
                          width: 24,
                          height: 24,
                          fontSize: 24,
                          color: stepColor,
                          transition: "color 150ms cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                      />
                    ) : index === inProgressIndex ? (
                      <Cached
                        sx={{
                          width: 24,
                          height: 24,
                          fontSize: 24,
                          color: stepColor,
                          transition: "color 150ms cubic-bezier(0.4, 0, 0.2, 1)",
                          transformOrigin: "center",
                        }}
                      />
                    ) : undefined
                  }
                  sx={{
                    "& .MuiStepLabel-labelContainer": {
                      width: "fit-content",
                      mx: isXs ? 0 : "auto",
                      px: 0.75,
                      py: 0.25,
                      borderRadius: "6px",
                      backgroundColor: isActive ? "rgba(194, 194, 194, 0.42)" : "transparent",
                      transform: isActive ? "scale(1.04)" : "scale(1)",
                      transformOrigin: "center",
                      transition: "background-color 150ms ease, transform 150ms ease",
                    },
                    "& .MuiStepLabel-iconContainer": {
                      width: 30,
                      height: 30,
                      p: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    },
                    "& .MuiStepLabel-label": {
                      fontWeight: isActive ? 600 : 500,
                      color: isCurrent ? colors.orange.base : isCompleted ? roleColors.residential.base : colors.gray[500],
                      transition: "color 150ms cubic-bezier(0.4, 0, 0.2, 1)",
                      "&.Mui-active, &.Mui-completed": {
                        color: isCurrent ? colors.orange.base : isCompleted ? roleColors.residential.base : colors.gray[500],
                      },
                    },
                    "& .MuiStepIcon-root": {
                      width: 24,
                      height: 24,
                      fontSize: "24px",
                      boxSizing: "border-box",
                      color: stepColor,
                      transition: "color 150ms cubic-bezier(0.4, 0, 0.2, 1)",
                      "&.Mui-active": {
                        color: stepColor,
                        fontSize: 24,
                      },
                      "&.Mui-completed": {
                        color: stepColor,
                      },
                    },
                  }}
                >
                  {isXs ? "" : step.label}

                  <Typography
                    variant="caption"
                    display="block"
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.6rem",
                      textTransform: "uppercase",
                      color: colors.gray[400],
                    }}
                  >
                    {statusText}
                  </Typography>
                </StepLabel>
                </Tooltip>
              </Step>
            );
          })}
        </Stepper>
      </Box>
    </Box>
  );
};
